import { useQuery } from '@tanstack/react-query';

import { getPropertyCoverages } from '@/shared/services';
import type { CoverageRule } from '@/shared/types';
import type { GetPropertyCoveragesParams } from '@/shared/services/coverage.service';

const QUERY_KEY = 'propertyCoverages';
const STALE_TIME = 10 * 60 * 1000;

type UseCoverageRulesOptions = {
  params?: GetPropertyCoveragesParams;
  waitForRequiredParams?: boolean;
};

/*
 * DDS rejects partial lease pricing params, so callers must wait until the
 * full pricing tuple is present before sending any of them.
 */
const hasAllRequiredPricingParams = (params?: GetPropertyCoveragesParams) =>
  Boolean(params?.lease_start_date && params?.lease_end_date && params?.gross_monthly_rent);

/*
 * Spread param values individually so the query key compares by value, not reference.
 */
const buildQueryKey = (propertyId: string, params?: GetPropertyCoveragesParams) => [
  QUERY_KEY,
  propertyId,
  params?.lease_start_date,
  params?.lease_end_date,
  params?.gross_monthly_rent,
  params?.prepaid_rent,
  params?.free_rent,
];

export const useCoverageRules = (
  propertyId: string,
  enabled: boolean = true,
  options?: UseCoverageRulesOptions,
) => {
  const hasAllRequiredParams = hasAllRequiredPricingParams(options?.params);
  const queryParams = hasAllRequiredParams ? options?.params : undefined;
  const waitingForParams = Boolean(options?.waitForRequiredParams) && !hasAllRequiredParams;
  const queryEnabled = Boolean(propertyId) && enabled && !waitingForParams;

  return useQuery<CoverageRule[]>({
    queryKey: buildQueryKey(propertyId, queryParams),
    queryFn: () => getPropertyCoverages(propertyId, queryParams),
    enabled: queryEnabled,
    staleTime: STALE_TIME,
    retry: false,
  });
};
